import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import { Info, Download, ExternalLink, DollarSign, RefreshCw, Link as LinkIcon } from "lucide-react";

/**
 * HarvardOfBoulder – Single‑page React view for setora.ai/harvardOfBoulder
 * Purpose: Compare products for mind clarity & longevity with subtle affiliate links.
 * Design: Tailwind + shadcn/ui + Recharts. No external fetching.
 * Routing: Add a route for "/harvardOfBoulder" and render <HarvardOfBoulder/>.
 */

// ---------- Types ----------
interface Benefits {
  energy: number;
  mood: number;
  clarity: number;
  memory: number;
  nootropicsDepth: number;
}

interface Product {
  key: string;
  name: string;
  category: string;
  typicalUse: string;
  keyActives: string;
  caffeine: string;
  benefits: Benefits;
  priceMin: number;
  priceMax: number;
  unitPrice?: number;
  unitsPerPack?: number;
  affiliate?: string;
}

interface PriceMidpoint {
  name: string;
  min: number;
  max: number;
  mid: number;
}

// ---------- Helpers ----------
const currency = (n: number | null | undefined): string => 
  (n == null || Number.isNaN(n) ? "–" : n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 }));

const scoreColor = (v: number): string => 
  (v >= 4 ? "bg-emerald-500" : v >= 3 ? "bg-sky-500" : v >= 2 ? "bg-amber-500" : "bg-rose-500");

interface AffLinkProps {
  href: string;
  label?: string;
  subtle?: boolean;
}

const AffLink: React.FC<AffLinkProps> = ({ href, label = "Shop", subtle = false }) => (
  <a
    href={href}
    target="_blank"
    rel="nofollow sponsored noopener noreferrer"
    className={
      subtle
        ? "inline-flex items-center gap-1 text-xs text-primary hover:underline"
        : "inline-flex items-center gap-2 text-xs rounded-full border px-2.5 py-1 hover:bg-primary/5"
    }
  >
    <LinkIcon className="h-3.5 w-3.5" /> {label}
  </a>
);

const DisclosureNote: React.FC = () => (
  <div className="mt-6 text-xs text-muted-foreground leading-relaxed">
    <p>
      Scores are qualitative estimates based on ingredient profiles and user‑reported effects, not clinical claims. Prices/promotions change.
      Consult a clinician for safety. Some links on this page are affiliate links that may earn a commission at no cost to you.
    </p>
  </div>
);

interface RadarBlockProps {
  p: Product;
}

const RadarBlock: React.FC<RadarBlockProps> = ({ p }) => {
  const data = [
    { metric: "Energy", value: p.benefits.energy },
    { metric: "Mood", value: p.benefits.mood },
    { metric: "Clarity", value: p.benefits.clarity },
    { metric: "Memory", value: p.benefits.memory },
    { metric: "Nootropics", value: p.benefits.nootropicsDepth },
  ];
  return (
    <ResponsiveContainer width="100%" height={260}>
      <RadarChart data={data} outerRadius={90}>
        <PolarGrid />
        <PolarAngleAxis dataKey="metric" />
        <PolarRadiusAxis angle={90} domain={[0, 5]} tickCount={6} />
        <Radar name={p.name} dataKey="value" stroke="#0f172a" fill="#0f172a" fillOpacity={0.3} />
      </RadarChart>
    </ResponsiveContainer>
  );
};

interface PriceBarsProps {
  rows: PriceMidpoint[];
}

const PriceBars: React.FC<PriceBarsProps> = ({ rows }) => (
  <ResponsiveContainer width="100%" height={280}>
    <BarChart data={rows} layout="vertical" margin={{ left: 16, right: 24, top: 8, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis type="number" tickFormatter={(v: number) => `$${v}`} domain={[0, Math.max(...rows.map((r) => r.max)) * 1.2]} />
      <YAxis type="category" dataKey="name" width={200} />
      <Tooltip formatter={(v: number) => `$${Number(v).toFixed(2)}`} />
      <Legend />
      <Bar dataKey="mid" name="Price/day (mid)" />
    </BarChart>
  </ResponsiveContainer>
);

interface EditablePriceProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
}

const EditablePrice: React.FC<EditablePriceProps> = ({ label, value, onChange }) => (
  <div className="flex items-center gap-2">
    <Label className="w-40 text-sm text-muted-foreground">{label}</Label>
    <div className="relative">
      <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input type="number" step="0.01" min="0" value={value} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(Number(e.target.value))} className="pl-7 w-32" />
    </div>
  </div>
);

interface ProductCardProps {
  p: Product;
  onUpdate: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ p, onUpdate }) => {
  const mid = useMemo(() => Number(((p.priceMin + p.priceMax) / 2).toFixed(2)), [p.priceMin, p.priceMax]);
  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span>{p.name}</span>
            <span className="text-sm font-normal text-muted-foreground">{p.category}</span>
          </div>
          <div className="flex items-center gap-2">
            {p.affiliate && <AffLink href={p.affiliate} label="Shop" />}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="space-y-3">
          <div className="text-sm"><span className="font-medium">Typical use:</span> {p.typicalUse}</div>
          <div className="text-sm"><span className="font-medium">Key actives:</span> {p.keyActives}</div>
          <div className="text-sm"><span className="font-medium">Caffeine:</span> {p.caffeine}</div>

          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { k: "Energy", v: p.benefits.energy },
              { k: "Mood", v: p.benefits.mood },
              { k: "Clarity", v: p.benefits.clarity },
              { k: "Memory", v: p.benefits.memory },
              { k: "Nootropics", v: p.benefits.nootropicsDepth },
            ].map((b) => (
              <div key={b.k} className="p-2 rounded-xl bg-slate-50 border">
                <div className="text-xs text-muted-foreground">{b.k}</div>
                <div className="mt-1 flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${scoreColor(b.v)}`} />
                  <div className="font-semibold">{b.v}/5</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 p-3 rounded-xl bg-amber-50 border text-sm">
            <div className="flex items-center gap-2"><Info className="h-4 w-4"/> Scores are qualitative estimates based on ingredient profiles, not clinical claims.</div>
          </div>
        </div>

        <div className="space-y-4">
          <Tabs defaultValue="inputs" className="w-full">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="inputs">Pricing Inputs</TabsTrigger>
              <TabsTrigger value="chart">Benefit Radar</TabsTrigger>
            </TabsList>
            <TabsContent value="inputs" className="space-y-3 pt-3">
              <EditablePrice label="Price/day min" value={p.priceMin} onChange={(v: number) => onUpdate({ ...p, priceMin: v })} />
              <EditablePrice label="Price/day max" value={p.priceMax} onChange={(v: number) => onUpdate({ ...p, priceMax: v })} />
              {p.unitsPerPack && (
                <div className="text-xs text-muted-foreground">
                  Example: {p.unitsPerPack} servings per pack at {currency(p.unitPrice)} list → {currency((p.unitPrice || 0) / (p.unitsPerPack || 1))} per serving.
                </div>
              )}
              <div className="mt-2 text-sm">
                <div>Computed mid: <span className="font-semibold">{currency(mid)}/day</span></div>
              </div>
            </TabsContent>
            <TabsContent value="chart" className="pt-3">
              <RadarBlock p={p} />
            </TabsContent>
          </Tabs>
        </div>
      </CardContent>
    </Card>
  );
};

interface ComparisonTableProps {
  rows: Product[];
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="bg-slate-50 text-left">
          <th className="py-2 px-3 font-semibold">Product</th>
          <th className="py-2 px-3">Energy</th>
          <th className="py-2 px-3">Mood</th>
          <th className="py-2 px-3">Mental Clarity</th>
          <th className="py-2 px-3">Memory</th>
          <th className="py-2 px-3">Nootropics</th>
          <th className="py-2 px-3">Price/day (min–max)</th>
          <th className="py-2 px-3">Link</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p: Product) => (
          <tr key={p.key} className="border-t">
            <td className="py-2 px-3 font-medium">{p.name}</td>
            <td className="py-2 px-3">{p.benefits.energy}/5</td>
            <td className="py-2 px-3">{p.benefits.mood}/5</td>
            <td className="py-2 px-3">{p.benefits.clarity}/5</td>
            <td className="py-2 px-3">{p.benefits.memory}/5</td>
            <td className="py-2 px-3">{p.benefits.nootropicsDepth}/5</td>
            <td className="py-2 px-3">{currency(p.priceMin)}–{currency(p.priceMax)}</td>
            <td className="py-2 px-3">{p.affiliate && <AffLink href={p.affiliate} label="Shop" subtle />}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DEFAULT_PRODUCTS: Product[] = [
  {
    key: "thesis",
    name: "Thesis (Own Your Mind)",
    category: "Nootropic blends (capsules)",
    typicalUse: "Daily capsules; blend varies by goal (Clarity, Energy, Stress, Neuroprotection)",
    keyActives: "Alpha-GPC, Lion's Mane, L-theanine, choline, flavonoids (varies by blend)",
    caffeine: "0 or optional caffeine capsule (varies by blend)",
    benefits: { energy: 4, mood: 3, clarity: 4, memory: 3, nootropicsDepth: 5 },
    priceMin: 3.29,
    priceMax: 5.38,
    affiliate: "https://rstyle.me/+OwjSxjCHVVMgZ0AWPdqeKQ",
  },
  {
    key: "ryze",
    name: "RYZE Superfoods (Mushroom Coffee)",
    category: "Functional coffee (powder)",
    typicalUse: "~1 cup/day (30 servings per bag)",
    keyActives: "Lion's Mane, Reishi, Cordyceps, Turkey Tail, King Trumpet, Shiitake + coffee, MCT",
    caffeine: "~48 mg (Medium roast) or ~80–90 mg (Dark)",
    benefits: { energy: 3, mood: 3, clarity: 3, memory: 2, nootropicsDepth: 2 },
    priceMin: 0.90,
    priceMax: 1.20,
    unitPrice: 36,
    unitsPerPack: 30,
    affiliate: "https://rstyle.me/+5xAMJacx3c0PMb9lylU7gQ",
  },
  {
    key: "littlesaints",
    name: "Little Saints (Variety Pack)",
    category: "Zero‑proof functional mocktails (cans)",
    typicalUse: "1 can as desired (evenings / social)",
    keyActives: "Reishi extract (≥70% beta‑glucans), botanical terpenes; 0 sugar ~5 kcal",
    caffeine: "0 (no caffeine)",
    benefits: { energy: 1, mood: 3, clarity: 1, memory: 1, nootropicsDepth: 1 },
    priceMin: 3.90,
    priceMax: 5.00,
    affiliate: "https://rstyle.me/+v27HGlMir4uYOxVZe0DdCw",
  },
  {
    key: "mudwater",
    name: "MudWater",
    category: "Mushroom chai/coffee alternative (powder)",
    typicalUse: "~1 cup/day (varies by blend)",
    keyActives: "Chaga, Reishi, Lion's Mane, Cordyceps + masala chai/cacao; typically low caffeine",
    caffeine: "Low (varies by blend)",
    benefits: { energy: 2, mood: 3, clarity: 2, memory: 2, nootropicsDepth: 2 },
    priceMin: 1.10,
    priceMax: 2.00,
    affiliate: "https://rstyle.me/+N5ZneLGIWqTVfEtcWPE8lg",
  },
];

const HarvardOfBoulder: React.FC = () => {
  const [products, setProducts] = useState<Product[]>(DEFAULT_PRODUCTS);

  const mids = useMemo(
    () =>
      products.map((p) => ({
        name: p.name,
        min: p.priceMin,
        max: p.priceMax,
        mid: Number(((p.priceMin + p.priceMax) / 2).toFixed(2)),
      })),
    [products]
  );

  const reset = () => setProducts(DEFAULT_PRODUCTS.map((p) => ({ ...p })));
  const printPage = () => window.print();

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Mind Clarity & Longevity — Product Comparison</h1>
          <p className="text-muted-foreground mt-1">Thesis vs RYZE vs Little Saints vs MudWater. Editable inputs. Live price/day. Printable.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={reset} className="gap-2"><RefreshCw className="h-4 w-4"/>Reset</Button>
          <Button onClick={printPage} className="gap-2"><Download className="h-4 w-4"/>Print</Button>
        </div>
      </div>

      {/* Inline affiliate row for subtle prominence */}
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span className="text-muted-foreground">Quick links:</span>
        <AffLink href="https://rstyle.me/+OwjSxjCHVVMgZ0AWPdqeKQ" label="Try Thesis" subtle />
        <AffLink href="https://rstyle.me/+5xAMJacx3c0PMb9lylU7gQ" label="Shop RYZE" subtle />
        <AffLink href="https://rstyle.me/+v27HGlMir4uYOxVZe0DdCw" label="Little Saints" subtle />
        <AffLink href="https://rstyle.me/+N5ZneLGIWqTVfEtcWPE8lg" label="MudWater" subtle />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6">
        {products.map((p) => (
          <ProductCard
            key={p.key}
            p={p}
            onUpdate={(next: Product) => setProducts((prev) => prev.map((x) => (x.key === next.key ? next : x)))}
          />
        ))}
      </div>

      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Price per day (midpoint) — Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <PriceBars rows={mids} />
            <div className="text-xs text-muted-foreground mt-2">Bar shows midpoint of the min–max range above. Adjust inputs in each card to update.</div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Side‑by‑Side Benefits</CardTitle>
          </CardHeader>
          <CardContent>
            <ComparisonTable rows={products} />
          </CardContent>
        </Card>
      </div>

      <DisclosureNote />

      <div className="mt-8 text-xs text-muted-foreground space-y-1">
        <div className="font-medium">Assumptions / Sources (high‑level):</div>
        <ul className="list-disc ml-5 space-y-1">
          <li>Thesis pricing commonly cited as ~$79/mo subscription or ~$119–$129 one‑time for ~24 "on" days. <AffLink href="https://rstyle.me/+OwjSxjCHVVMgZ0AWPdqeKQ" label="Try Thesis" subtle /></li>
          <li>RYZE bag ~30 servings; typical site/list ~$27–$36. Caffeine ~48 mg (Medium), ~80–90 mg (Dark). <AffLink href="https://rstyle.me/+5xAMJacx3c0PMb9lylU7gQ" label="Shop RYZE" subtle /></li>
          <li>Little Saints Variety Pack ~12–48 packs. <AffLink href="https://rstyle.me/+v27HGlMir4uYOxVZe0DdCw" label="Little Saints" subtle /></li>
          <li>MudWater pricing varies by blend and subscription. <AffLink href="https://rstyle.me/+N5ZneLGIWqTVfEtcWPE8lg" label="MudWater" subtle /></li>
        </ul>
      </div>

      <div className="mt-10 mb-16 text-center text-sm text-muted-foreground">
        <a className="inline-flex items-center gap-1 hover:underline" href="https://setora.ai/harvardOfBoulder" target="_blank" rel="noreferrer">
          setora.ai/harvardOfBoulder <ExternalLink className="h-3.5 w-3.5"/>
        </a>
      </div>
    </div>
  );
};

export default HarvardOfBoulder;